import { useEffect, useState } from "react";
import { categories, stories, type Story } from "../../../mocks/stories";

interface StoryModalProps {
  story: Story | null;
  onClose: () => void;
  onOpenStory: (s: Story) => void;
}

export default function StoryModal({ story, onClose, onOpenStory }: StoryModalProps) {
  const [liked, setLiked] = useState(false);
  const [collected, setCollected] = useState(false);

  useEffect(() => {
    if (!story) return;
    setLiked(false);
    setCollected(false);
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [story, onClose]);

  if (!story) return null;

  const cur = stories.findIndex((s) => s.id === story.id);
  const next = stories[(cur + 1) % stories.length];
  const prev = stories[(cur - 1 + stories.length) % stories.length];
  const cat = categories.find((c) => c.key === story.cat);

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center p-0 sm:items-center sm:p-6">
      <div className="absolute inset-0 bg-foreground-950/60 animate-fade" onClick={onClose}></div>
      <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[2rem] bg-background-50 shadow-2xl sm:rounded-[2rem] animate-modal">
        {/* header image */}
        <div className="relative shrink-0">
          <img src={story.image} alt={`${story.title}现场配图`} className="h-52 w-full object-cover object-top md:h-64" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent"></div>
          <button
            onClick={onClose}
            aria-label="关闭弹窗"
            className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-background-50/90 text-foreground-700 hover:bg-background-50"
          >
            <i className="ri-close-line"></i>
          </button>
          <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-500 px-3 py-1.5 text-xs font-semibold text-foreground-950">
              <span aria-hidden="true">{cat?.emoji}</span> {story.tag}
            </span>
            <span className="flex items-center gap-1 text-xs text-background-50/90">
              <i className="ri-eye-line"></i> {story.reads} 围观
            </span>
          </div>
        </div>

        {/* body scroll */}
        <div className="overflow-y-auto px-5 py-5 md:px-8 md:py-6">
          <h3 className="font-heading text-2xl leading-snug text-foreground-950 md:text-[1.7rem]">{story.title}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-smile-line"></i> 网友投稿整理
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i> {story.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i> 弹幕 {Math.floor(story.likes / 12)} 条
            </span>
          </div>

          <article className="mt-5 space-y-4">
            {story.body.map((p, i) => (
              <p key={i} className="text-[15px] leading-[1.9] text-foreground-700 md:text-base">
                {p}
              </p>
            ))}
          </article>

          <div className="mt-6 flex items-center justify-between rounded-2xl bg-background-100 p-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setLiked((v) => !v)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  liked ? "bg-primary-500 text-background-50" : "bg-background-50 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={`${liked ? "ri-emotion-laugh-fill" : "ri-emotion-laugh-line"}`}></i>
                笑一个 {story.likes + (liked ? 1 : 0)}
              </button>
              <button
                onClick={() => setCollected((v) => !v)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  collected ? "bg-accent-500 text-foreground-950" : "bg-background-50 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={`${collected ? "ri-star-fill" : "ri-star-line"}`}></i>
                {collected ? "已收藏" : "收藏这个瓜"}
              </button>
            </div>
            <button
              onClick={onClose}
              className="hidden items-center gap-1.5 whitespace-nowrap rounded-full bg-foreground-950 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:flex"
            >
              收起 <i className="ri-arrow-up-s-line"></i>
            </button>
          </div>
        </div>

        {/* prev / next nav */}
        <div className="flex shrink-0 items-stretch gap-2 border-t border-background-200 p-3">
          <button
            onClick={() => onOpenStory(prev)}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-xl bg-background-100 px-3 py-2.5 text-left transition-colors hover:bg-background-200"
          >
            <i className="ri-arrow-left-s-line shrink-0 text-xl text-foreground-400"></i>
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] text-foreground-400">上一条爆梗</span>
              <span className="block truncate text-sm font-medium text-foreground-800">{prev.title}</span>
            </span>
          </button>
          <button
            onClick={() => onOpenStory(next)}
            className="flex min-w-0 flex-1 items-center justify-end gap-2 rounded-xl bg-background-100 px-3 py-2.5 text-right transition-colors hover:bg-background-200"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] text-foreground-400">下一条爆梗</span>
              <span className="block truncate text-sm font-medium text-foreground-800">{next.title}</span>
            </span>
            <i className="ri-arrow-right-s-line shrink-0 text-xl text-foreground-400"></i>
          </button>
        </div>
      </div>
    </div>
  );
}